import React, { useEffect, useState, useCallback, Suspense } from 'react';
import LoaderComponent from '@components/atom/layout/loader';
import { useSelector } from 'react-redux';
import { RootState } from '@reducers/index';
import { QueryHookParam, useQueryDummyHook, useQueryHook } from '@utils/hook/useQueryHook';
import { CreateReviewChartOption } from '@classes/options/chart/app/review';
import {
  ColumnChartDataModel,
  AverageRatingDataModel,
  PieChartDataModel,
  WordCloudDataModel,
} from '@models/highChartsModel';
import moment from 'moment';

const AppInfo = React.lazy(() => import('@components/template/layout/appInfo/appInfo'));
const DailyReviewCount = React.lazy(() => import('@components/template/app/review/dailyReviewCount'));
const AverageRating = React.lazy(() => import('@components/template/app/review/averageRating'));
const ReviewsChart = React.lazy(() => import('@components/template/app/review/reviews'));
const WordCloudBox = React.lazy(() => import('@components/template/app/review/wordCloudBox'));
const ReviewBox = React.lazy(() => import('@components/template/app/review/reviewBox'));

type Props = {};

const Review = (props: Props) => {
  const { selectedAppInfo } = useSelector((state: RootState) => state.LAYOUT);
  // 차트가 공유하는 선택된 날짜
  const [selectedDate, setSelectedDate] = useState(null);

  // chartData
  const [dailyChartData, setDailyChartData] = useState<ColumnChartDataModel>(null);
  const [averageChartData, setAverageChartData] = useState<AverageRatingDataModel[]>(null);
  const [reviewsChartData, setReviewsChartData] = useState<PieChartDataModel>(null);
  const [wordCloudChartData, setWordCloudChartData] = useState<WordCloudDataModel>(null);
  const [reviewList, setReviewList] = useState(null);

  // wordCloud tab
  const [tabState, setTabState] = useState<'summary' | 'newWords'>('summary');
  const [wordCloudParams, setWordCloudParams] = useState<{
    appId: any;
    provider_type: any;
    date?: any;
    tabType: string;
  }>(null);

  // reviewList Params, moreButton active
  const [reviewListParams, setReviewListParams] = useState<{ appId; provoder_type; page }>(null);
  const [moreButtonActive, setMoreButtonActive] = useState<boolean>(true);

  // chartDataOption
  const _option = new CreateReviewChartOption();

  // Daily, Average, Review 차트 데이터 더미데이터
  const ratingParams: QueryHookParam = {
    key: ['dailyAverageReviewBox', selectedAppInfo?.appId],
    url: '/api/app/appProfileReviewRating.json',
    params: { appId: selectedAppInfo?.appId, type: selectedAppInfo?.provider_type },
    method: 'GET',
    enabled: selectedAppInfo !== null,
  };
  const { data: ratingData, isLoading: isLoadingRatingData } = useQueryDummyHook(ratingParams);
  // // Daily, Average, Review 차트 데이터 API 데이터
  // const ratingParams: QueryHookParam = {
  //   key: ['dailyAverageReviewBox', selectedAppInfo?.appId],
  //   url: '/app/review/dailyReviewCount',
  //   params: {
  //     appId: selectedAppInfo?.appId,
  //     provider_type: selectedAppInfo?.provider_type,
  //     startDate: '2022-08-10',
  //     endDate: '2022-08-19',
  //   },
  //   method: 'GET',
  //   enabled: selectedAppInfo !== null,
  // };
  // const { data: ratingData, isLoading: isLoadingRatingData } = useQueryHook(ratingParams);

  // wordCloud 차트 데이터 아래는 더미데이터 관련
  const wordParams: QueryHookParam = {
    key: ['wordCloudData', selectedAppInfo?.appId, tabState, selectedDate],
    url: tabState === 'summary' ? '/api/app/appReviewwordClode1.json' : '/api/app/appReviewwordClode2.json',
    params: wordCloudParams,
    method: 'GET',
    enabled: wordCloudParams !== null,
  };
  const { data: wordCloudData } = useQueryDummyHook(wordParams);
  // // wordCloud 차트 데이터 아래는 API 데이터
  // const wordParams: QueryHookParam = {
  //   key: ['wordCloudData', selectedAppInfo?.appId, tabState, selectedDate],
  //   url: '/app/review/wordcloud',
  //   params: wordCloudParams,
  //   method: 'GET',
  //   enabled: wordCloudParams !== null,
  // };
  // const { data: wordCloudData } = useQueryHook(wordParams);

  // reviewList 데미 데이터
  const reviewListHookParams: QueryHookParam = {
    key: ['reviewListData', selectedAppInfo?.appId, reviewListParams?.page],
    url: reviewListParams?.page === 0 ? '/api/app/appReviewList1.json' : '/api/app/appReviewList2.json',
    params: reviewListParams,
    method: 'GET',
    enabled: reviewListParams !== null,
  };
  const { data: reviewListData, isLoading: isLoadingReviewListData } = useQueryDummyHook(reviewListHookParams);
  // // reviewList API 데이터
  // const reviewListHookParams: QueryHookParam = {
  //   key: ['reviewListData', selectedAppInfo?.appId, reviewListParams?.page],
  //   url: '/scraper/review',
  //   params: reviewListParams,
  //   method: 'GET',
  //   enabled: reviewListParams !== null,
  // };
  // const { data: reviewListData, isLoading: isLoadingReviewListData } = useQueryHook(reviewListHookParams);

  // Daily, Average, Review 차트 데이터 옵션처리
  useEffect(() => {
    if (ratingData) {
      setDailyChartData(_option.dailyReviewCount(ratingData, dateChangeHandler));
      setAverageChartData(_option.averageRating(ratingData));
      setReviewsChartData(_option.reviews(ratingData));
      setSelectedDate(
        moment(ratingData.length > 0 ? ratingData[0].baseDate : moment().subtract(1, 'days')).format('YYYY-MM-DD')
      );
      setWordCloudParams({
        appId: selectedAppInfo.appId,
        provider_type: selectedAppInfo.provider_type,
        date: moment(ratingData.length > 0 ? ratingData[0].baseDate : moment().subtract(1, 'days')).format(
          'YYYY-MM-DD'
        ),
        tabType: tabState,
      });
    }
  }, [ratingData]);

  // wordCloud 차트 데이터 옵션처리
  useEffect(() => {
    if (wordCloudData) {
      setWordCloudChartData(_option.wordcloud(wordCloudData, wordCloudParams.date));
    }
  }, [wordCloudData]);

  // reviewList 데이터 옵션처리
  useEffect(() => {
    if (reviewListData) {
      // 데이터 갯수가 8개보다 작으면 more 버튼 비활성화처리
      if (reviewListData.length < 8) {
        setMoreButtonActive(false);
      } else {
        setMoreButtonActive(true);
      }
      setReviewList((prev) => {
        if (!prev) {
          return reviewListData;
        } else {
          return prev.concat(reviewListData);
        }
      });
    }
  }, [reviewListData]);

  // reviewListData 가 존재하고 selectedAppInfo 가 바뀔때 reviewListData 초기화 처리
  useEffect(() => {
    if (selectedAppInfo) {
      if (reviewList) {
        setReviewList(null);
        setMoreButtonActive(true);
      }
      setReviewListParams({
        appId: selectedAppInfo.appId,
        provoder_type: selectedAppInfo.provider_type,
        page: 0,
      });
    }
  }, [selectedAppInfo]);

  useEffect(() => {}, [wordCloudParams]);

  const dateChangeHandler = useCallback((date) => {
    setSelectedDate(date);
    setWordCloudParams((prev) => ({ ...prev, date: date }));
  }, []);

  const tabHandler = useCallback((tabState) => {
    setTabState(tabState);
    setWordCloudParams((prev) => ({ ...prev, tabType: tabState }));
  }, []);

  const reviewListMoreHandler = useCallback(() => {
    setReviewListParams((prev) => ({
      ...prev,
      page: prev.page + 1,
    }));
  }, []);

  return (
    <>
      <div className="app-info">
        <Suspense fallback={<LoaderComponent />}>
          <AppInfo />
        </Suspense>
      </div>
      <Suspense fallback={<LoaderComponent />}>
        <DailyReviewCount chartData={dailyChartData} />
      </Suspense>
      <div className="score-promotion-version review">
        <Suspense fallback={<LoaderComponent />}>
          <AverageRating chartData={averageChartData} />
        </Suspense>
        <Suspense fallback={<LoaderComponent />}>
          <ReviewsChart chartData={reviewsChartData} isLoading={isLoadingRatingData} />
        </Suspense>
      </div>
      <Suspense fallback={<LoaderComponent />}>
        <WordCloudBox chartData={wordCloudChartData} currentTab={tabState} tabHandler={tabHandler} />
      </Suspense>
      <Suspense fallback={<LoaderComponent />}>
        <ReviewBox
          reviewList={reviewList}
          moreButtonActive={moreButtonActive}
          moreButtonHandler={reviewListMoreHandler}
          isLoading={isLoadingReviewListData}
        />
      </Suspense>
    </>
  );
};

export default Review;
